<template>
  <div class="card tweet-card compose-card"
       :class="tweetboxClass"
       v-resize="resizeEditor"
       @dragover="checkImageDrag"
       @dragexit="showDropTarget=false">
    <div class="droptarget is-size-3" v-show="showDropTarget"
         @drop.prevent="dropFile"
         @dragover.prevent
         @dragleave="showDropTarget=false"
         @dragend="showDropTarget=false"
    >drop images here
    </div>

    <div class="card-content">
      <div class="media">
        <tweet-media-left
            :show-line-up="!this.isFirst"
            :show-line-down="!this.isLast"
            :show-grabber="true">
          <profile-pic v-if="this.index === 0" :user="$store.state.auth.profile" :no-click="true"></profile-pic>
          <span v-else class="subtitle is-4 has-text-grey">{{ index + 1 }}</span>
        </tweet-media-left>

        <div class="media-content">
          <div class="edit-area field" ref="editarea">
            <div class="editarea is-relative">
              <!-- make sure no whitespace between tags here! -->
              <div class="htmlarea" aria-hidden="true">
                <span class="has-text-grey" :style="{ width: textboxIndent, position: 'absolute' }">{{ numberingPrefix }}</span>
                <div class="main-text" :style="{ textIndent: textboxIndent }">
                  <span v-html="valueAllowedAutoLinked"></span><span
                  class="has-background-danger has-opacity-25">{{ valueExcess }}</span><span
                  class="has-text-grey">{{ numberingSuffix }}</span>
                </div>
              </div>
              <label>
                <textarea
                    v-model="textAndSuffix"
                    class="hidden-editor"
                    :style="{
                      textIndent: textboxIndent
                    }"
                    ref="hiddeneditor" disabled></textarea>
              </label>
              <label><textarea
                  :id="'tweettextarea' + id"
                  @blur="handleBlur"
                  @paste="handlePaste"
                  @mousedown="$store.dispatch('switchActiveTweet', index)"
                  v-focus="hasFocus"
                  ref="editor"
                  class="editor is-relative"
                  v-model="text"
                  :placeholder="getPlaceholder"
                  :style="{
                    height: editorHeight,
                    textIndent: textboxIndent
                  }"
                  @keydown="handleKeydown"
              /></label>

              <a v-if="canDelete"
                 class="delete"
                 @click="remove()"
                 :title="`${$modifierKeyEncoded}-Del: Delete this tweet from the thread.`"
              ></a>

            </div>
          </div>
          <div class="tools-area">

            <editor-images :tweet-index="index"/>
            <tweet-link
                v-if="!images.length && previewUrl && isLoggedIn && $store.getters.settings('compose', 'viewLink')"
                :link="previewUrl"
                :load-on-mount="true"/>

            <input type="file" accept="image/*" class="is-hidden"
                   ref="fileinput" @change="addImagesFromInput" multiple/>
            <editor-tools
                ref="tools"
                :index="this.index"
                :remaining-chars="remainingChars"
                :tweet-permillage="tweet.permillage"
                @addImage="showAddImageDialog"
                @showEmojis="showEmojis"
            />

            <div v-if="$store.getters.getQuoteTweet(index)" class="quoted-tweet mb-4">
              <tweet
                  :show-delete="true"
                  :data="$store.getters.getQuoteTweet(index)"
                  :mini-view="true"
                  :collapsed="!$store.getters.settings('compose', 'viewQuote')"
                  @clear="$store.commit('clearQuoteTweet', index)"
              />
            </div>
            <editor-buttons
                @addTweet="$emit('addTweetBelow')"
                @breakTweet="$emit('addTweetBelow', cutTextAtCursor().trim())"
                @flowDown="flowDown()"
                :is-bottom="isLast"/>
          </div>

        </div>
      </div>
    </div>

  </div>

</template>

<script>
import resize from 'vue-resize-directive'
import twitterText from 'twitter-text'
import {Textarea, Textcomplete} from 'textcomplete'
import _ from 'lodash'

import tweets from "../../api/tweets"
import EditorButtons from "./EditorButtons";
import Tweet from "../tweet/Tweet";
import EditorTools from "./EditorTools";
import EditorImages from "./EditorImages";
import TweetLink from "../tweet/TweetLink";
import TweetMediaLeft from "../TweetMediaLeft";
import ProfilePic from "../ProfilePic";
import getValidTweetFromText from "../../utils/tweettext";
import {createImageFromFile} from "../../store/modules/images";

let putInputInView = (el) => {
  let pos = el.closest('.card').getBoundingClientRect()
  const navbar = 56;
  const margin = 10;
  if (pos.height < window.innerHeight) {  // only proceed if the box is small enough to actually fit in the window
    if (pos.y < navbar) {
      window.scrollBy(0, pos.y - navbar - margin)
    } else if (pos.bottom > window.innerHeight) {
      window.scrollBy(0, pos.bottom - window.innerHeight)
    }
  }
  el.focus()
}

// place to store our last delete snackback notification
let deleteNotification = undefined

export default {
  name: 'TweetBox',
  directives: {
    resize,
    'focus': (el, binding) => {
      if (binding.value) {
        setTimeout(() => putInputInView(el), 0);
      }
    }
  },

  components: {
    ProfilePic,
    TweetMediaLeft,
    EditorImages,
    EditorTools,
    Tweet,
    EditorButtons,
    TweetLink,
  },

  mounted() {
    this.buildTextComplete()
    this.onTextUpdate() // need to run this as text may be set on mount

    // if mounted with smartpaste, deal with it immediately and clear
    if (this.smartPaste) {
      this.handleSmartPaste(this.smartPaste)
      this.smartPaste = undefined
    }
  },

  beforeDestroy() {
    this.isBeingDestroyed = true
  },

  destroyed() {
    this.textcomplete && this.textcomplete.destroy()
  },

  props: {
    index: {type: Number},
    id: {type: String},
    warningLimit: {
      type: Number,
      default: 20,
    },
    isFirst: {
      type: Boolean,
      default: false
    },
    isLast: {
      type: Boolean,
      default: false
    },
    hasFocus: {
      type: Boolean,
      default: false
    }
  },

  data: function () {
    return {
      showDropTarget: false,
      textcomplete: undefined,
      isBeingDestroyed: false,
      modalImage: null,
      editorHeight: '20px',
    }
  },

  computed: {
    isLoggedIn() {
      return this.$store.getters.isLoggedIn
    },
    text: {
      get() {
        return this.$store.getters.tweets[this.index].text
      },
      set(value) {
        this.$store.dispatch('changeTweetText', {index: this.index, text: value})
      }
    },

    images() {
      return this.$store.getters.tweetImages(this.index)
    },

    smartPaste: {
      get() {
        return this.$store.getters.tweets[this.index].smart_paste
      },
      set(val) {
        this.$store.commit('setSmartPaste', {
          index: this.index,
          text: undefined
        })
      }
    },

    errorStatus: {
      get() {
        return this.$store.getters.tweets[this.index].error
      },
      set(val) {
        // doing these comparisons first may not actually be faster
        // but it stops polluting the vuex event log when there's
        // no actual updates, which is useful at lesat for debugging
        if (val !== this.errorStatus) {
          this.$store.dispatch('setTweetError', {
                index: this.index, error: val
              }
          )
        }
      }
    },

    previewUrl() {
      return this.urls.length ? {'url': this.urls.slice(-1)[0]} : undefined
    },

    urls: {
      get() {
        return this.$store.getters.tweets[this.index].urls
      },
      set(val) {
        if (!_.isEqual(val, this.urls)) {
          this.$store.commit('setTweetUrls', {
            index: this.index, urls: val
          })
        }
      }
    },

    mentions: {
      get() {
        return this.$store.getters.tweets[this.index].mentions
      },
      set(val) {
        if (!_.isEqual(val, this.mentions)) {
          this.$store.commit('setTweetMentions', {
            index: this.index, mentions: val
          })
        }
      }
    },

    canDelete() {
      // you can delete any tweet if >1 tweet or if not empty
      return (this.$store.getters.tweets.length > 1 || this.errorStatus !== this.$tweetErrorEnum.EMPTY)
    },

    getPlaceholder() {
      return this.index === 0
          ? 'Start your twitter thread...'
          : 'Continue your thread...'
    },

    numberingPrefix() {
      return this.$store.getters.tweetNumbering(this.index, true)
    },
    numberingSuffix() {
      return this.text ? this.$store.getters.tweetNumbering(this.index, false) : ''
    },

    textboxIndent() {
      // returns css for the length of the text + 'ch' - character
      // width of the o letter
      return (this.numberingPrefix.length * 0.8) + 'ch'
    },

    autoText() {
      // this represents all added text onto our tweet
      return this.$store.getters.tweetPrefix(this.index) +
          this.$store.getters.tweetSuffix(this.index)
    },

    fullText() {
      // this represents all text - written by the user, and
      // anything auto. We put all that at the start (even tho
      // some might be suffixed) to make calculations later easier
      return this.autoText + this.text
    },

    textAndSuffix() {
      // this is the full text and just the suffix - this is for the hidden-editor for sizing
      // the prefix is already acommodated with the indent
      return this.text + this.$store.getters.tweetSuffix(this.index)
    },

    tweet() {
      return twitterText.parseTweet(this.fullText);
    },

    tweetboxClass() {
      return {
        'tweet-empty': this.errorStatus === this.$tweetErrorEnum.EMPTY,
        'tweet-too-long': this.errorStatus === this.$tweetErrorEnum.TOO_LONG,
        'tweet-warning': (this.errorStatus !== this.$tweetErrorEnum.TOO_LONG && this.remainingChars <= this.warningLimit),
      }
    },

    valueAllowed() {
      return this.tweet
          ? this.fullText.slice(this.tweet.validRangeStart + this.autoText.length, this.tweet.validRangeEnd + 1)
          : '';
    },
    valueAllowedAutoLinked() {
      return twitterText.autoLink(this.valueAllowed)
    },
    valueExcess() {
      return this.tweet ? this.fullText.slice(this.tweet.validRangeEnd + 1) : '';
    },
    remainingChars() {
      return 280 - this.tweet.weightedLength;
    }
  },

  updated: function () {
    this.resizeEditor()
  },

  watch: {
    text: 'onTextUpdate',
    images() {
      this.updateErrorStatus()
    },
    editorHeight() {
      // when the editor height changes, we should make sure the box is still fully visible
      setTimeout(() => putInputInView(this.$el), 0)
    },
  },

  methods: {
    handleKeydown(e) {
      if (e.code.toLowerCase() === 'tab') {
        if (e.shiftKey) {
          this.$store.dispatch('focusOnTweet', this.index - 1)
        } else {
          this.$store.dispatch('focusOnTweet', this.index + 1)
        }
        e.preventDefault()
        return
      }
      if (e.code.toLowerCase().startsWith('digit') && (e.metaKey || e.ctrlKey)) {
        this.$store.dispatch('focusOnTweet', parseInt(e.code.substr(5), 10) - 1)
        e.preventDefault()
        return
      }

      // special case for the enter key
      if (e.code.toLowerCase() === 'enter') {
        if (e.shiftKey && (e.metaKey || e.ctrlKey)) {
          this.$store.commit('setTrigger', 'postThread')
          e.preventDefault()
          return
        } else if (e.metaKey || e.ctrlKey) {
          this.$emit('addTweetBelow')
          e.preventDefault()
          return
        }
      }

      // ctrl-shift-s for scheduling
      if ((e.code.toLowerCase() === 'keys') &&
          (e.shiftKey && (e.metaKey || e.ctrlKey))) {
        this.$store.commit('setTrigger', 'scheduleThread')
        e.preventDefault()
        return
      }

      // handle all other ctrl combos
      if (e.metaKey || e.ctrlKey) {
        if (e.altKey) {
          switch (e.code.toLowerCase()) {
            case 'keyt':
              this.$store.dispatch('createNewThread')
              break

            default:
              return

          }
          e.preventDefault()

        } else {
          switch (e.code.toLowerCase()) {
            case 'keye':
              this.showEmojis()
              break

            case 'keyr':
              this.$emit('focusOnFirstError')
              break

            case 'arrowdown':
              this.$store.dispatch('focusOnTweet', this.index + 1)
              break

            case 'arrowup':
              this.$store.dispatch('focusOnTweet', this.index - 1)
              break

            case 'home':
              this.$store.dispatch('focusOnTweet', 0)
              break

            case 'end':
              this.$store.dispatch('focusOnTweet', -1)
              break

            case 'keyi':
              this.$emit('addTweetBelow', this.cutTextAtCursor().trim())
              break

            case 'keyf':
              this.flowDown()
              break

            case 'keyh':
              this.$emit('showHotkeyModal')
              break


            case 'delete':
              this.remove()
              break

            default:
              return
          }
          e.preventDefault()
        }
      }

    },

    onTextUpdate() {
      this.updateErrorStatus()
      this.urls = twitterText.extractUrls(this.text)
      this.mentions = twitterText.extractMentions(this.text)
    },

    updateErrorStatus() {

      let isImageGif = (image) => {
        // TODO - this includes static images. Twitter API does allow up to 4 gifs if not animated. We
        // can try sniffing for this with e.g. https://gist.github.com/zakirt/faa4a58cec5a7505b10e3686a226f285
        return image.type === 'image/gif'
      }

      let gifs = this.images.filter(image => isImageGif(image))

      // too many images
      if (this.images.length > 4) {
        this.errorStatus = this.$tweetErrorEnum.TOO_MANY_IMAGES

        // all gifs, and we have more than one
      } else if (this.images.length === gifs.length && this.images.length > 1) {
        this.errorStatus = this.$tweetErrorEnum.TOO_MANY_GIFS

        // just one gif, but other images too
      } else if (this.images.length > 1 && gifs.length === 1) {
        this.errorStatus = this.$tweetErrorEnum.GIFS_MIXED_WITH_IMAGES

        // tweet too long or short
      } else if (this.text.length === 0 && !this.images.length) { // only empty if not text *or* images
        this.errorStatus = this.$tweetErrorEnum.EMPTY
      } else if (this.tweet.weightedLength && !this.tweet.valid) {
        this.errorStatus = this.$tweetErrorEnum.TOO_LONG
      } else {
        this.errorStatus = null
      }
    },

    addImage(file) {
      createImageFromFile(file).then(img => {
        this.$store.dispatch('addNewImageToTweet', {
          tweet_index: this.index,
          image_data: img
        })
      })
    },

    addImagesFromInput(event) {
      event.target.files.forEach(item => {
        this.addImage(item)
      })
    },

    handlePaste(event) {
      let data = (event.clipboardData || window.clipboardData)

      // try pasting in images
      if (data.items.length) {
        data.items.forEach(item => {
          let file = item.getAsFile()
          if (file) {
            this.addImage(file);
          }
        })
      }

      // also see if we're empty, see if there's text - if there
      // is then we do a smart paste with text and prevent default

      // TODO no undo on paste command
      if (this.text.length === 0) {
        let text = data.getData('Text')
        if (text) {
          event.preventDefault()
          this.handleSmartPaste(text)
        }
      }
    },

    handleSmartPaste(text) {
      // we set the text and if there's anything left over,
      // we insert it into a tweet below - this will trigger a waterfall
      // inserting tweets until it is all used up.
      let remainder
      [this.text, remainder] = getValidTweetFromText(text, this.autoText)
      if (remainder) {
        this.$store.dispatch('addTweetWithSmartPaste', {
          index: this.index + 1, text: remainder
        });
      }
    },

    showAddImageDialog() {
      this.$refs.fileinput.click()
    },

    checkImageDrag(e) {
      if (!this.showDropTarget) {
        if (e.dataTransfer && e.dataTransfer.items.length && (e.dataTransfer.types.includes('Files') || e.dataTransfer.getData('image-id'))) {
          this.showDropTarget = true
          e.preventDefault()
        }
      }
    },


    dropFile(e) {
      this.showDropTarget = false;

      if (e.dataTransfer.dropEffect !== 'copy' && e.dataTransfer.dropEffect !== 'move') {
        // known bug on chrome and safari - not set on drop
        //https://stackoverflow.com/questions/19010257/event-datatransfer-dropeffect-in-chrome/43892407
        e.dataTransfer.dropEffect = 'move'
      }

      // this is for a file (from the user's computer) dropped here
      if (e.dataTransfer && e.dataTransfer.files.length) {
        for (let i = 0; i < e.dataTransfer.files.length; i++) {
          this.addImage(e.dataTransfer.files[i])
        }

        // this handles other items like elements and text on the page
      } else if (e.dataTransfer.items.length) {

        let tweet_index = parseInt(e.dataTransfer.getData('tweet-index'))
        let image_id = e.dataTransfer.getData('image-id')


        if (!isNaN(tweet_index) && image_id) {

          // if image already exists, back out
          if (_.includes(this.$store.getters.tweets[this.index].image_ids, image_id)) {
            return
          }

          this.$store.dispatch('addExistingImageToTweet', {
            tweet_index: this.index,
            image_id: image_id,
          })

          if (e.dataTransfer.dropEffect === 'move') {
            this.$store.dispatch('removeImageFromTweet', {
              tweet_index: tweet_index,
              image_id: image_id
            })
          }
        }

      }
    },


    insertAtCursor(textToInsert) {
      const isSuccess = document.execCommand("insertText", false, textToInsert);
      let input = this.$refs.editor;

      // Firefox (non-standard method)
      if (!isSuccess && typeof input.setRangeText === "function") {
        const start = input.selectionStart;
        input.setRangeText(textToInsert);
        // update cursor to be at the end of insertion
        input.selectionStart = input.selectionEnd = start + textToInsert.length;

        // Notify any possible listeners of the change
        const e = document.createEvent("UIEvent");
        e.initEvent("input", true, false);
        input.dispatchEvent(e);
      }
    },

    resizeEditor: function () {
      // sets the height of the editor to how many scrolls of our hidden 1-line editor
      this.editorHeight = this.$refs.hiddeneditor.scrollHeight + 'px'
    },

    cutTextAtCursor() {
      let input = this.$refs.editor;
      let starter = input.value.slice(0, input.selectionStart);
      let remainder = input.value.slice(input.selectionStart, input.value.length);
      this.$store.dispatch('changeTweetText', {index: this.index, text: starter.trim()})
      return remainder
    },

    showEmojis() {
      putInputInView(this.$el)
      this.$store.dispatch('switchActiveTweet', this.index)
      this.$emit('showEmojis')
    },

    flowDown() {
      if (!this.isLast) { // protect again this firing via hotkey, even if button disabled
        this.$emit('insertTextBelow', this.cutTextAtCursor().trim())
      }
    },

    remove() {
      deleteNotification && deleteNotification.close() // if we have a previous one, kill it so they dont stack
      this.$store.dispatch('removeTweet', this.index)
      deleteNotification = this.$buefy.snackbar.open({
        message: 'Tweet deleted.',
        position: 'is-top',
        queue: false,
        actionText: 'Undo',
        type: 'is-danger',
        duration: 5000,
        onAction: () => {
          this.$store.dispatch('undeleteTweet')
        }
      })
    },

    handleBlur() {
      this.$store.dispatch('defocusTweet')
      this.textcomplete.hide()
    },

    buildTextComplete() {
      let editor = new Textarea(this.$refs.editor);
      this.textcomplete = new Textcomplete(editor);

      // According to https://yuku.takahashi.coffee/jquery-textcomplete/ you customize this
      // in an object passed as the second param in `register` but this didn't work for me
      this.textcomplete.dropdown.itemOptions.className = 'dropdown-item'
      this.textcomplete.dropdown.el.className = 'dropdown-content'
      this.textcomplete.dropdown.placement = 'auto'

      // this is a debounced global search on the twitter API, which gets
      // appended to our local results once complete. We check the
      let addGlobalSearch = _.debounce((term, callback, local) => {
        return tweets.userSearch(term).then(response => {
          // only add in names that don't already exist in our local dataset
          let localNames = local.map(f => f.screen_name)
          let globals = response.data.filter(f => !_.includes(localNames, f.screen_name))
          callback(local.concat(globals))
        })
      }, 500)

      // this does a search for a user. First it does a filter on
      // local friends, and it also debounces a call to the Twitter API
      // to append in some more matches at the bottom
      let searchFriends = (friends, keyword) => {
        let score = (f) => {
          // this scores a friend based on a simple algorithm where names that start or
          // end exactly do best (and if its an exact match, it gets best points) -
          // and ultimately ranking by word length (shorter the better)
          // this relies on true === 1, false === 0 when coercsed
          return (_.startsWith(f.screen_name.toLocaleLowerCase(), keyword) * 100) +
              (_.startsWith(f.name.toLocaleLowerCase(), keyword) * 50) +
              (_.endsWith(f.screen_name.toLocaleLowerCase(), keyword) * 20) +
              (_.endsWith(f.name.toLocaleLowerCase(), keyword) * 20) +
              (10 - f.screen_name.length)
        }

        // filter friends by matching sure something matches, and then return it ordered by our scoring algo
        let filteredFriends = friends.filter(f =>
            f.screen_name.toLowerCase().includes(keyword) ||
            f.name.toLowerCase().includes(keyword)
        )
        return _.orderBy(filteredFriends, score, 'desc')
      }

      let mentionsSearch = (term, callback) => {
        if (this.isLoggedIn) {
          term = term.toLowerCase().trim()
          let friends = searchFriends(this.$store.state.auth.friends || [], term).slice(0, 10)
          callback(friends)
          addGlobalSearch(term, callback, friends)
        }
      }

      let emojiSearch = (term, callback) => {
        // TODO - superhack!! calling the function 2 parents up (threadis) to call emoji search
        // but alternative of going thru vuex seems wrong too and emiting/watching events/props also weird
        callback(this.$parent.$parent.searchEmojis(term, 10))
      }

      this.textcomplete.register([
            {
              id: 'mentions',
              match: /(^|\s)@([A-Za-z0-9_]+)$/,
              search: mentionsSearch,
              template: function (user) {
                // bit of a hack - if this came from the twitter API then
                // we have loads more meta-data, and its a global account,
                // not from our friend list
                let user_type = 'global'
                if (user.followers_count === undefined) {
                  user_type = 'friend'
                  user.image_url = user.image_url_mini
                }
                return `
              <div class="media media-user-${user_type}">
                <div class="media-left"><figure class="image is-24x24">
                  <img src="${user.image_url}" class="is-rounded" alt="${user.screen_name} profile picture">
                </figure></div>
                <div class="media-content">
                  <strong>${user.name}</strong> <small>@${user.screen_name}</small><br>
                </div>
              </div>`
              },
              replace: function (user) {
                return '$1@' + user.screen_name + ' ';
              }
            },
            {
              id: 'emojis',
              index: 1,
              match: /:([A-Za-z0-9_-]+)$/,
              search: _.debounce(emojiSearch, 50),
              template: function (emoji) {
                return `
              <div class="media">
                <figure class="media-left"><div class="image is-32424 is-size-5">${emoji.native}</div></figure>
                <div class="media-content">
                  <strong>${emoji.short_name.replace(/_/gi, ' ')}</strong> <small>${emoji.colons}</small><br>
                  <small><em>${emoji.name}</em></small>
                </div>
              </div>
						`
              },
              replace: function (emoji) {
                return emoji.native;
              }
            }
          ]
      )
    }
  },
};
</script>

<style lang="scss">

.slide-fade-enter-active {
  transition: all .1s ease;
}

.slide-fade-leave-active {
  transition: all .1s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}


.compose-card {
  /* make shadow stronger than default */
  box-shadow: 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.15), 0 0px 0 1px rgba(10, 10, 10, 0.1) !important;
  border-bottom: solid 1px transparent;

  .tweet-too-long h3,
  .tweet-empty h3 {
    /* bulma danger colors
      border-color: #f14668;
      color: #cc0f35; */

    color: #cc0f35 !important;
  }

  .field-tools .level-left,
  .field-tools .level-left .level-grow {
    flex-grow: 1;
  }

  .field-tools .level-left .level-grow {
    padding-right: 1rem;
  }

  .quote-tweet {
    width: 100%;
  }

  .field-tools,
  .field-buttons {
    transition: opacity 0.1s;
    opacity: 0.2;

    &:hover {
      opacity: 1;
    }

  }

  transition: box-shadow 0.1s, border-bottom 0.1s;

  &:focus-within {
    box-shadow: 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.15), 0 0px 0 1px rgba(10, 255, 10, 0.5) !important;
    border-bottom: solid 1px rgba(10, 255, 10, 0.25);

    .delete {
      opacity: 0.5;
    }

    .field-tools,
    .field-buttons {
      opacity: 0.7;

      &:hover {
        opacity: 1;
      }
    }
  }
}


.media-content {
  overflow-x: inherit !important;
}

.card .media-content .delete {
  position: absolute;
  right: 0;
  top: 0;
  opacity: 0.5;

  &:hover {
    opacity: 1;
  }
}

.htmlarea,
.editor,
.hidden-editor {
  /* normalize html area and textarea */
  color: rgb(74, 74, 74);
  min-height: 2rem;
  border: 0;
  padding-right: 1.5rem;
  font-size: 1rem;
  font-stretch: 100%;
  letter-spacing: 0;
  width: 100%;
  line-height: 1.5rem;
}

.hidden-editor {
  height: 1.25rem; /* height of hidden editor set to 1 line height */
  opacity: 0;
  position: absolute;
  overflow-y: scroll; /* always show a scrollbar, otherwise sometimes it will report fewer lines than we need */
}

.htmlarea {
  position: absolute;
  height: 100%;
  white-space: pre-wrap;
  word-wrap: break-word;

  .main-text {
    color: white;
  }
  a {
    /* underlines will be visible thru the text area! */
    text-decoration: underline;
  }
}


.editor {
  overflow: hidden; /* bing needs this so scroll bar doesn't show */
  display: block;
  border-color: lightgray;
  outline: 0;
  background-color: transparent;
  resize: none;

  &:focus {
    border-color: #9b4dca;
  }
}

.tweet-too-long .editor {
  border-color: red;
}

.tweet-warning .editor {
  border-color: darkorange;
}


.droptarget {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 5;
  background-color: grey;
  opacity: 0.4;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  text-transform: uppercase;
  font-weight: 300;
  letter-spacing: 0.2rem;
  border: dashed 2px white;
}

/* unfortunately hard-coded in to `active` and an A tag so we have to manually use this (see dropdown_item.js.flow L40) */
li.dropdown-item a {
  color: #4a4a4a;
}

li.dropdown-item.active {
  background-color: #f5f5f5;

  a {
    color: #0a0a0a;
  }
}


</style>
